'use client';

import React, { useState, useCallback } from 'react';
import { SoundboardItem as SoundboardItemType } from '@/utils/soundboardStorage';
import { useSoundboard } from '@/contexts/SoundboardManager';
import { FaPlay, FaPause, FaTrash, FaGripVertical } from 'react-icons/fa';

interface SoundboardItemProps {
  item: SoundboardItemType;
  onPlay: (item: SoundboardItemType) => void;
  isPlaying?: boolean;
  isDragging?: boolean;
  dragHandleProps?: any;
}

const SoundboardItem: React.FC<SoundboardItemProps> = ({
  item,
  onPlay,
  isPlaying = false,
  isDragging = false,
  dragHandleProps
}) => {
  const { removeItem } = useSoundboard();
  const [isHovered, setIsHovered] = useState(false);
  const handlePlay = useCallback((e: React.MouseEvent) => {
    console.log('🎵 SoundboardItem handlePlay clicked for:', {
      itemName: item.name,
      itemId: item.id,
      hasOnPlay: !!onPlay
    });
    
    e.stopPropagation();
    onPlay(item);
  }, [item, onPlay]);

  const handleDelete = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    await removeItem(item.id);
  }, [item.id, removeItem]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds || isNaN(seconds)) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className={`
        relative group bg-slate-700 rounded-lg p-3 border border-slate-600
        transition-all duration-200 ease-in-out
        ${isDragging ? 'opacity-50 scale-95' : 'hover:bg-slate-600 hover:border-slate-500'}
        ${isPlaying ? 'ring-2 ring-blue-500 bg-blue-900/20' : ''}
        cursor-pointer
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handlePlay}
    >
      {/* Drag Handle */}
      <div
        {...dragHandleProps}
        className={`
          absolute top-1 left-1 p-1 rounded cursor-grab active:cursor-grabbing
          transition-opacity duration-200
          ${isHovered ? 'opacity-100' : 'opacity-0'}
          hover:bg-slate-500/50
        `}
        title="Drag to reorder"
      >
        <FaGripVertical className="w-3 h-3 text-slate-400" />
      </div>

      {/* Play Button */}
      <div className="flex items-center justify-center mb-2">
        <button
          className={`
            w-8 h-8 rounded-full flex items-center justify-center
            transition-all duration-200
            ${isPlaying 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
            }
            ${isHovered ? 'scale-110' : 'scale-100'}
          `}
          onClick={handlePlay}
          title={isPlaying ? 'Playing...' : 'Play sound'}
        >
          {isPlaying ? (
            <FaPause className="w-3 h-3" />
          ) : (
            <FaPlay className="w-3 h-3 ml-0.5" />
          )}
        </button>
      </div>

      {/* Sound Info */}
      <div className="text-center">
        <h4 className="text-sm font-medium text-white truncate mb-1" title={item.name}>
          {item.name}
        </h4>
        
        <div className="text-xs text-slate-400 space-y-1">
          {item.duration && (
            <div>{formatDuration(item.duration)}</div>
          )}
          <div>{formatFileSize(item.fileSize)}</div>
        </div>
      </div>

      {/* Delete Button */}
      <button
        className={`
          absolute top-1 right-1 p-1 rounded
          transition-all duration-200
          ${isHovered ? 'opacity-100' : 'opacity-0'}
          bg-slate-600 hover:bg-red-600 text-slate-300 hover:text-white
        `}
        onClick={handleDelete}
        title="Delete sound"
      >
        <FaTrash className="w-3 h-3" />
      </button>

      {/* Playing Indicator */}
      {isPlaying && (
        <div className="absolute inset-0 rounded-lg border-2 border-blue-500 animate-pulse pointer-events-none" />
      )}
    </div>
  );
};

export default SoundboardItem;
